import { EmptyState, SectionCard, TokenUsageIcon } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';

/**
 * The drawer explains the dependency, because "no numbers" has two very different
 * causes and a client cannot tell them apart from an empty screen: nobody has used any
 * AI yet, or nothing is counting. Here it is the second, and saying so is the point.
 */
const TOKEN_USAGE_HELP: HelpContent = {
  title: 'Token Usage',
  icon: '🧾',
  purpose:
    'Will account for <strong>LLM token consumption</strong> across the platform &mdash; what was spent, on which model, by which module and by whom. <strong>Nothing is counted yet</strong>, so the screen is deliberately empty rather than showing a zero that looks like a measurement.',
  doHere: [
    'Nothing yet. Once usage is recorded, this is where it is read &mdash; it is a reporting screen, never a place to set a budget or a limit.',
  ],
  fits: 'Administration &rarr; Token Usage',
  firstTime:
    'If you are here to find out what the AI features have cost, the answer is that the platform is not yet recording it. An empty screen here does not mean nothing has been spent.',
  sections: [
    {
      type: 'html',
      label: 'Why it is empty',
      html: 'Counting tokens has to happen where the model is called, and every AI call in this platform will go through one shared gateway so there is a single place that records usage. <strong>That gateway is not built yet.</strong> Until it is there is no honest number to show, and inventing one on an accounting screen would be worse than showing nothing.',
    },
    {
      type: 'defs',
      label: 'What it will break the spend down by',
      items: [
        {
          term: 'Model',
          def: 'Tokens and cost per model. Models differ in price by more than an order of magnitude, so this is usually the line that explains a bill.',
        },
        {
          term: 'Module',
          def: 'Which product consumed it &mdash; APR, Opportunity Registry, ERP Consolidation and so on. This is the breakdown that says whether a feature is worth what it costs.',
        },
        {
          term: 'User',
          def: 'Who ran the work. Useful for spotting one person re-running an expensive analysis, not for policing individuals.',
        },
      ],
      note:
        'Input and output tokens are priced differently by every provider, so they are counted separately rather than added together.',
    },
    {
      type: 'html',
      label: 'Related, but not this screen',
      html: 'Which <strong>provider</strong> each module uses is set under <em>Administration &rarr; Open AI Integration</em>. That screen decides where the calls go; this one will report what they cost. Neither of them limits spend &mdash; a cap is enforced by the provider account itself, in the client&rsquo;s own Anthropic, Azure, Google or OpenAI console.',
    },
  ],
};

/**
 * Token Usage — LLM token accounting, lifted from AI Readiness.
 *
 * Declared rather than stubbed: it depends on packages/llm-gateway (Phase 1). Until
 * the gateway records usage there is nothing truthful to show, and inventing numbers
 * on an accounting screen would be worse than an honest empty state.
 */
export function TokenUsageTab() {
  return (
    <>
      <HelpPanel help={TOKEN_USAGE_HELP} />
      <SectionCard>
        <EmptyState
          icon={<TokenUsageIcon />}
          title="LLM token accounting is not wired up yet"
          description="This screen lifts AI Readiness's token-usage accounting — spend per model, per module and per user. It depends on packages/llm-gateway, which arrives in Phase 1 alongside the shared engines."
        />
      </SectionCard>
    </>
  );
}
